import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SortDirection } from "@/lib/table-sort";

export type { SortDirection };

export interface SortableTableHeaderProps {
  label: string;
  // Sort key sent to `onSort`, and compared against `orderBy`.
  column: string;
  orderBy?: string;
  orderDir?: SortDirection;
  onSort: (column: string) => void;
  className?: string;
}

// Sortable column header, per the design: an unsorted column reveals a neutral
// up/down chevron on hover; the sorted column shows a persistent directional
// chevron, and its label stays darkened.
//
// Requires the enclosing `<th>` to carry `group` for the hover states —
// `DataTable` does this for every header cell.
export function SortableTableHeader({
  label,
  column,
  orderBy,
  orderDir,
  onSort,
  className,
}: SortableTableHeaderProps): React.ReactElement {
  const active = orderBy === column;

  return (
    <button
      type="button"
      className={cn(
        "flex max-w-full min-w-0 cursor-pointer items-center gap-1 border-none bg-transparent p-0 font-medium transition-colors select-none",
        "group-hover:text-foreground",
        active && "text-foreground",
        className
      )}
      onClick={() => onSort(column)}
    >
      <span className="truncate">{label}</span>
      {active ? (
        orderDir === "asc" ? (
          <ChevronUp size={16} className="shrink-0" />
        ) : (
          <ChevronDown size={16} className="shrink-0" />
        )
      ) : (
        <ChevronsUpDown
          size={16}
          className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
        />
      )}
    </button>
  );
}
